import { useEffect, useState } from "react";
import { Link , useParams} from "react-router-dom";

import { presetPalettes } from "@ant-design/colors";
import { Card, Col, Row, Space, Empty } from "antd";

import { formatDistanceToNow } from "date-fns";
import type { LatLngTuple, PointTuple } from "leaflet";
import { Popup } from "react-leaflet";
import { Doughnut } from "react-chartjs-2";

import {
    DeviceData, DevicesSummary, Site
} from "../../model/model";

import SessionStore from "../../model/SessionStore";
import type { MarkerColor } from "../../components/Map";
import Map, { Marker } from "../../components/Map";


interface DeviceProps {
  summary?: DevicesSummary;
}

interface DeviceMapProps {
  items: DeviceData[];
}

function DevicesMap(props: DeviceMapProps) {
  if (props.items.length === 0) {
    return <Empty />;
  }

  const boundsOptions: {
    padding: PointTuple;
  } = {
    padding: [50, 50],
  };

  const bounds: LatLngTuple[] = [];
  const markers: JSX.Element[] = [];

  for (const item of props.items) {
    if (item.locLat === undefined) {
      continue;
    }

    const pos: LatLngTuple = [item.locLat, item.locLong];
    bounds.push(pos);

    let color: MarkerColor = "orange";
    let lastSeen: string = "Never seen online";

    if (!item.online) {
      color = "red";
    } else {
      color = "green";
    }

    if (item.lastSeenAt !== undefined) {
      lastSeen = formatDistanceToNow(item.lastSeenAt, { addSuffix: true });
    }

    markers.push(
      <Marker key={item.id} position={[pos[0], pos[1]]} faIcon="wifi" color={color}>
        <Popup>
          <Link to={`/device/${item.id}`}>{item.name}</Link>
          <br />
          {item.id}
          <br />
          <br />
          {lastSeen}
        </Popup>
      </Marker>,
    );
  }

  return (
    <Map height={500} bounds={bounds} boundsOptions={boundsOptions}>
      {markers}
    </Map>
  );
}


function DevicesActiveInactive(props: DeviceProps) {
  if (
    props.summary === undefined ||
    (props.summary.neverSeenCount === 0 &&
      props.summary.inactiveCount === 0 &&
      props.summary.activeCount === 0)
  ) {
    return <Empty />;
  }

  const data = {
    labels: ["Never seen", "Inactive", "Active"],
    datasets: [
      {
        data: [props.summary.neverSeenCount, props.summary.inactiveCount, props.summary.activeCount],
        backgroundColor: [presetPalettes.orange.primary, presetPalettes.red.primary, presetPalettes.green.primary],
      },
    ],
  };

  const options: {
    animation: boolean;
    responsive: boolean;
    maintainAspectRatio: boolean;
  } = {
    animation: false,
    responsive: true,
    maintainAspectRatio: false,
  };

  return (
    <div className="chart-doughnut">
      <Doughnut data={data} options={options} />
    </div>
  );
}

function DevicesDataRates(props: DeviceProps) {
  const getColor = (dr: number) => {
    return [
      "#ff5722",
      "#ff9800",
      "#ffc107",
      "#ffeb3b",
      "#cddc39",
      "#8bc34a",
      "#4caf50",
      "#009688",
      "#00bcd4",
      "#03a9f4",
      "#2196f3",
      "#3f51b5",
      "#673ab7",
      "#9c27b0",
      "#e91e63",
    ][dr];
  };
  return <Empty />;

}

function SiteDashboard({ site }: { site: Site }) {
  const [devicesSummary, setDevicesSummary] = useState<DevicesSummary | undefined>(undefined);
 const [deviceItems, setDeviceItems] = useState<DeviceData[]>([]);

  useEffect(() => {
    {
      SessionStore.getClient().getDevicesSummary(site.id, SessionStore.getBearer()).then( (resp) => {
        setDevicesSummary(resp.data);
      });
      SessionStore.getClient().getSiteDevices(site.id, SessionStore.getBearer()).then( (resp) => {
        setDeviceItems(resp.data);
      });
    }
  }, [site]);

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      <Row gutter={24}>
        <Col span={8}>
          <Card title="Active devices">
            <DevicesActiveInactive summary={devicesSummary} />
          </Card>
        </Col>
        <Col span={8}>
          <Card title="Device data-rate usage">
            <DevicesDataRates summary={devicesSummary} />
          </Card>
        </Col>
      </Row>
      <Card title="Device map">
        <DevicesMap items={deviceItems} />
      </Card>
    </Space>
  );
}

export default SiteDashboard;
